import React, { useMemo, useEffect, useState } from "react";
import { X, Loader2, Brain } from "lucide-react";
import { PriceAlertEventData } from "@/api/price-alerts";
import { usePriceAlertSymbolHistory } from "@/hooks/usePriceAlerts";
import { useTokenMetadata } from "@/hooks/useTokenMetadata";
import { useTokenAvailability, useTokenData } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { PriceAlertHistoryList } from "@crypto-trading/ui-shared";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { TokenIcon } from "@/components/TokenIcon";
import { ensureUsdtSymbol, extractBaseCurrency } from "@/utils/symbols";

interface PriceAlertAnalyzeSheetDesktopProps {
  userId: string;
  event: PriceAlertEventData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: (symbol: string) => void;
}

interface SheetFooterProps {
  isCheckingAvailability: boolean;
  hasTradableExchange: boolean;
  hasExchanges: boolean;
  onTrade?: () => void;
  baseCurrency: string;
}

const SheetFooter: React.FC<SheetFooterProps> = ({
  isCheckingAvailability,
  hasTradableExchange,
  hasExchanges,
  onTrade,
  baseCurrency,
}) => {
  if (isCheckingAvailability) {
    return (
      <div className="flex items-center justify-center py-3 text-text-secondary text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Checking availability...
      </div>
    );
  }

  if (hasTradableExchange && onTrade) {
    return (
      <button
        type="button"
        onClick={onTrade}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-700 transition-colors"
      >
        Trade {baseCurrency}
      </button>
    );
  }

  const message = hasExchanges
    ? "Connect an exchange to trade this token"
    : "This token is not available on supported exchanges";

  return (
    <div className="text-center py-3">
      <p className="text-sm text-text-secondary">{message}</p>
    </div>
  );
};

const SHEET_WIDTH = 500;

const formatPrice = (price: number | null | undefined) => {
  if (!price) return "N/A";
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
};

const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h24",
  });
};

export const PriceAlertAnalyzeSheetDesktop: React.FC<
  PriceAlertAnalyzeSheetDesktopProps
> = ({ userId, event, open, onOpenChange, onTrade }) => {
  const symbol = event?.symbol ?? "";
  const normalizedSymbol = useMemo(
    () => (symbol ? ensureUsdtSymbol(symbol) : ""),
    [symbol]
  );
  const baseCurrency = useMemo(
    () => (symbol ? extractBaseCurrency(symbol) : ""),
    [symbol]
  );

  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const { data: tokenMetadata, isLoading: isLoadingMetadata } =
    useTokenMetadata(baseCurrency, open);
  const { data: symbolHistory = [], isLoading: isLoadingHistory } =
    usePriceAlertSymbolHistory(symbol, open);
  const { data: exchanges = [] } = useUserExchanges(userId, true);
  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, open);
  const { data: tokenData } = useTokenData(
    userId,
    normalizedSymbol,
    undefined,
    open && !!normalizedSymbol
  );

  const globallyAvailableExchangeCodes = useMemo(
    () => tokenAvailability?.userExchanges ?? [],
    [tokenAvailability?.userExchanges]
  );
  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );
  const supportedUserExchangeCodes = useMemo(
    () =>
      exchanges
        .filter(
          (e) => !e.code.endsWith("-demo") && !e.code.endsWith("-testnet")
        )
        .map((e) => e.code)
        .filter((code) => globallyAvailableSet.has(code)),
    [exchanges, globallyAvailableSet]
  );
  const hasTradableExchange = supportedUserExchangeCodes.length > 0;
  const isCheckingAvailability = isLoadingAvailability;

  useEffect(() => {
    if (open && event) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = "hidden";
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, event]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    if (open) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onOpenChange]);

  if (!event || !isVisible) return null;

  const isSpike = event.alertType.toLowerCase() === "spike";

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 bg-black/60 z-[100] transition-opacity duration-300 border-0 cursor-default"
        style={{ opacity: isAnimating ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
        aria-label="Close analysis sheet"
        tabIndex={-1}
      />

      <div
        className="fixed top-0 right-0 h-full bg-secondary border-l border-border flex flex-col shadow-2xl z-[101] transition-transform duration-300 ease-out"
        style={{
          width: `${SHEET_WIDTH}px`,
          transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <TokenIcon
              symbol={baseCurrency}
              logo={tokenMetadata?.logo}
              size="sm"
            />
            <div>
              <p className="text-xs text-text-secondary">
                Price Alert Analysis
              </p>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-text-primary">
                  {baseCurrency}
                </h2>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${
                    isSpike
                      ? "bg-green-500/20 text-green-400"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {isSpike ? "Spike" : "Drop"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {tokenData?.lastPrice && (
              <div className="text-right">
                <p className="text-xs text-text-secondary">Live Price</p>
                <p className="text-sm font-semibold text-text-primary">
                  {formatPrice(tokenData.lastPrice)}
                </p>
              </div>
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg hover:bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Alert Summary */}
          <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-4">
            <p className="text-xs uppercase tracking-wide text-text-secondary">
              Alert Details
            </p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xs text-text-secondary mb-1">Change</p>
                <p
                  className={`text-sm font-semibold ${event.changePercent >= 0 ? "text-green-400" : "text-red-400"}`}
                >
                  {event.changePercent >= 0 ? "+" : ""}
                  {event.changePercent.toFixed(2)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">Start Price</p>
                <p className="text-sm font-semibold text-text-primary">
                  {formatPrice(event.startPrice)}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">
                  Current Price
                </p>
                <p className="text-sm font-semibold text-text-primary">
                  {formatPrice(event.currentPrice)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-center">
              <div>
                <p className="text-xs text-text-secondary mb-1">Timeframe</p>
                <p className="text-sm font-semibold text-text-primary">
                  {event.interval}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">Date</p>
                <p className="text-sm font-semibold text-text-primary">
                  {formatDateTime(event.timestamp)}
                </p>
              </div>
            </div>
          </div>

          {/* Market Data */}
          {tokenData && (
            <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-4">
              <p className="text-xs uppercase tracking-wide text-text-secondary">
                Market Data
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-text-secondary mb-1">Last Price</p>
                  <p className="text-sm font-semibold text-text-primary">
                    {formatPrice(tokenData.lastPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">24h High</p>
                  <p className="text-sm font-semibold text-green-400">
                    {formatPrice(tokenData.highPrice)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary mb-1">24h Low</p>
                  <p className="text-sm font-semibold text-red-400">
                    {formatPrice(tokenData.lowPrice)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Token Metadata */}
          {isLoadingMetadata && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-accent" />
            </div>
          )}
          {tokenMetadata &&
            (tokenMetadata.description || tokenMetadata.urls) && (
              <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-3">
                <p className="text-xs uppercase tracking-wide text-text-secondary">
                  Token Information
                </p>
                {tokenMetadata.description && (
                  <p className="text-sm text-text-primary leading-relaxed">
                    {tokenMetadata.description}
                  </p>
                )}
                {tokenMetadata.urls && (
                  <div className="flex flex-wrap gap-2">
                    {tokenMetadata.urls.website?.[0] && (
                      <a
                        href={tokenMetadata.urls.website[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        Website
                      </a>
                    )}
                    {tokenMetadata.urls.twitter?.[0] && (
                      <a
                        href={tokenMetadata.urls.twitter[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        Twitter
                      </a>
                    )}
                    {tokenMetadata.urls.source_code?.[0] && (
                      <a
                        href={tokenMetadata.urls.source_code[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        GitHub
                      </a>
                    )}
                    {tokenMetadata.urls.explorer?.[0] && (
                      <a
                        href={tokenMetadata.urls.explorer[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors border border-border"
                      >
                        Explorer
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

          {(isLoadingHistory || symbolHistory.length > 0) && (
            <PriceAlertHistoryList
              variant="analyze"
              title={`Alert History (${symbolHistory.length})`}
              events={symbolHistory.map((h) => ({
                id: h.id,
                alertType: h.alertType,
                exchange: h.exchange,
                changePercent: h.changePercent,
                interval: h.interval,
                timestamp: h.timestamp,
                currentPrice: h.currentPrice ?? null,
              }))}
              isLoading={isLoadingHistory}
            />
          )}

          {/* Exchange Availability */}
          {!isLoadingAvailability &&
            globallyAvailableExchangeCodes.length > 0 && (
              <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-3">
                <p className="text-xs uppercase tracking-wide text-text-secondary">
                  Exchange Availability
                </p>
                <div className="flex flex-wrap gap-2">
                  {globallyAvailableExchangeCodes.map((code) => (
                    <span
                      key={code}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                        supportedUserExchangeCodes.includes(code)
                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                          : "bg-green-500/10 text-green-400 border border-green-500/20"
                      }`}
                    >
                      {code}
                      {supportedUserExchangeCodes.includes(code) && " \u2713"}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Price Chart */}
          <div>
            <h3 className="text-sm font-bold text-text-primary mb-3">
              Live Price Chart
            </h3>
            <TradingViewChartWidget
              key={`chart-${normalizedSymbol}`}
              symbol={normalizedSymbol}
            />
          </div>

          {/* AI Analysis */}
          <div>
            <h3 className="text-sm font-bold text-text-primary flex items-center gap-2 mb-3">
              <Brain className="w-4 h-4 text-accent" />
              AI Analysis
            </h3>
            <TokenAnalysisSection tokenSymbol={baseCurrency} />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-secondary border-t border-border p-4 space-y-2">
          <SheetFooter
            isCheckingAvailability={isCheckingAvailability}
            hasTradableExchange={hasTradableExchange}
            hasExchanges={globallyAvailableExchangeCodes.length > 0}
            onTrade={
              onTrade
                ? () => {
                    onTrade(normalizedSymbol);
                    onOpenChange(false);
                  }
                : undefined
            }
            baseCurrency={baseCurrency}
          />
        </div>
      </div>
    </>
  );
};
